import { StagingInfoDialog } from "@/features/stagings/StagingInfoDialog";
import { baseUrl } from "@/lib/http";
import type { Furniture, FurnitureItem, Room } from "@/types/api";
import { FURNITURE_STATUS } from "@/types/api";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { inventoryApi } from "./api";
import { furnitureKeys } from "./queryKeys";
import { SearchHelpDialog } from "./SearchHelpDialog";

function formatSize(f: Furniture): string {
  const parts: string[] = [];
  if (f.width != null) parts.push(`${f.width}W`);
  if (f.height != null) parts.push(`${f.height}H`);
  if (f.length != null) parts.push(`${f.length}D`);
  return parts.join(" ") || "—";
}

function roomLabel(room: Room | null | undefined): string {
  if (!room) return "—";
  const addr = room.stagingInfo?.address ?? "";
  const name = room.name ?? "";
  return addr ? `${addr} (${name})` : name || "—";
}

function itemLocation(it: FurnitureItem): string {
  if (it.roomInfo) return roomLabel(it.roomInfo);
  if (it.locationInfo) {
    const cat = it.locationInfo.categoryInfo?.name ?? "";
    const parts = [cat, it.locationInfo.bin, it.locationInfo.slot].filter(Boolean);
    return parts.join(" ") || "—";
  }
  return "—";
}

const DETAIL_PAGE_SIZES = [10, 30, 100];

type DetailFilter = "in" | "out" | "selected" | "all";

interface DetailsDialogProps {
  category: string | null;
  filter: DetailFilter;
  search: string | null;
  onClose: () => void;
}

function DetailsDialog({ category, filter, search, onClose }: DetailsDialogProps) {
  const nav = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [previewImg, setPreviewImg] = useState<{ src: string; name: string } | null>(null);
  const [infoStagingId, setInfoStagingId] = useState<number | null>(null);

  // Reset to the first page whenever the search/category/filter/pageSize changes
  // so paging never points past the end of a freshly-narrowed result set.
  useEffect(() => {
    setPage(1);
  }, [search, category, filter, pageSize]);

  const skip = (page - 1) * pageSize;

  const itemsQuery = useQuery({
    queryKey: furnitureKeys.categoryItems(search, category, filter, skip, pageSize),
    queryFn: () =>
      inventoryApi.listCategoryItems({
        search: search ?? undefined,
        category: category ?? "",
        filter,
        skip,
        limit: pageSize,
      }),
    enabled: category !== null,
    placeholderData: (prev) => prev,
  });

  if (!category) return null;

  const pageItems: FurnitureItem[] = itemsQuery.data?.items ?? [];
  const total = itemsQuery.data?.total ?? 0;

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        {category}
        {filter !== "all"
          && ` — ${
            filter === "in" ? "In Storage" : filter === "out" ? "Out (Staged)" : "Selected"
          }`}
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 1.5, sm: 3 } }}>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: { xs: 720, sm: 0 } }}>
            <TableHead>
              <TableRow>
                <TableCell>IMAGE</TableCell>
                <TableCell>NAME</TableCell>
                <TableCell>SIZE</TableCell>
                <TableCell>STATUS</TableCell>
                <TableCell>CURRENT LOCATION</TableCell>
                <TableCell>RESERVED LOCATION</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {pageItems.map((item) => {
                const furniture: Furniture | undefined = item.furnitureInfo ?? undefined;
                const thumbUrl = furniture?.imageFile?.thumbnailPath40
                  ? `${baseUrl}${furniture.imageFile.thumbnailPath40}`
                  : null;
                const fullUrl = furniture?.imageFile?.relativePath
                  ? `${baseUrl}${furniture.imageFile.relativePath}`
                  : thumbUrl;
                const stagingId = item.roomInfo?.stagingInfo?.id ?? null;
                const displayName = furniture?.name ?? "—";

                return (
                  <TableRow key={item.id} hover>
                    <TableCell sx={{ width: 56 }}>
                      {thumbUrl
                        ? (
                          <Box
                            component="img"
                            src={thumbUrl}
                            alt={displayName}
                            onClick={() =>
                              fullUrl && setPreviewImg({ src: fullUrl, name: displayName })}
                            sx={{
                              width: 40,
                              height: 40,
                              objectFit: "cover",
                              borderRadius: 1,
                              cursor: "pointer",
                            }}
                          />
                        )
                        : (
                          <Box
                            sx={{ width: 40, height: 40, bgcolor: "grey.200", borderRadius: 1 }}
                          />
                        )}
                    </TableCell>
                    <TableCell>{displayName}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {furniture ? formatSize(furniture) : "—"}
                    </TableCell>
                    <TableCell>
                      {FURNITURE_STATUS[item.status as keyof typeof FURNITURE_STATUS]
                        ?? item.status}
                    </TableCell>
                    <TableCell
                      onClick={() =>
                        item.roomInfo
                        && void nav(
                          `/staging/${item.roomInfo.staging}/room-builder/${item.roomInfo.id}`,
                        )}
                      sx={{
                        cursor: item.roomInfo ? "pointer" : "default",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={item.roomInfo
                          ? {
                            color: "primary.main",
                            fontWeight: 500,
                            "&:hover": { textDecoration: "underline" },
                          }
                          : undefined}
                      >
                        {itemLocation(item)}
                      </Typography>
                    </TableCell>
                    <TableCell
                      onClick={() =>
                        item.reservationInfo
                        && void nav(
                          `/staging/${item.reservationInfo.staging}/room-builder/${item.reservationInfo.id}`,
                        )}
                      sx={{
                        cursor: item.reservationInfo ? "pointer" : "default",
                        "&:hover": item.reservationInfo ? { bgcolor: "action.hover" } : undefined,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={item.reservationInfo
                          ? {
                            color: "primary.main",
                            fontWeight: 500,
                            "&:hover": { textDecoration: "underline" },
                          }
                          : undefined}
                      >
                        {roomLabel(item.reservationInfo)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ width: 48 }}>
                      <Stack direction="row">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => void nav(`/administration/inventory/${item.furniture}`)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {stagingId != null
                          ? (
                            <Tooltip title="Staging Info">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => setInfoStagingId(stagingId)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )
                          : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {itemsQuery.isLoading && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              )}
              {!itemsQuery.isLoading && pageItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary">No items</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: { xs: 1, sm: 2 },
            mt: 1,
          }}
        >
          <Typography variant="body2">Page:</Typography>
          <Typography variant="body2">{page}</Typography>
          <Typography variant="body2">Rows per page:</Typography>
          <Select
            size="small"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            variant="standard"
          >
            {DETAIL_PAGE_SIZES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="body2" sx={{ mx: 1 }}>
            {total === 0
              ? "0"
              : `${skip + 1} - ${Math.min(skip + pageSize, total)} of ${total}`}
          </Typography>
          <IconButton size="small" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            size="small"
            disabled={skip + pageSize >= total}
            onClick={() => setPage((p) => p + 1)}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        </Box>
      </DialogContent>

      <StagingInfoDialog stagingId={infoStagingId} onClose={() => setInfoStagingId(null)} />

      <Dialog open={!!previewImg} onClose={() => setPreviewImg(null)} maxWidth="md">
        <DialogContent sx={{ p: 0, position: "relative", bgcolor: "white" }}>
          <IconButton
            size="small"
            onClick={() => setPreviewImg(null)}
            sx={{ position: "absolute", top: 8, right: 8, zIndex: 1, bgcolor: "white" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          {previewImg && (
            <Box sx={{ p: 2, pt: 5, textAlign: "center" }}>
              <Typography variant="subtitle1" gutterBottom>
                {previewImg.name}
              </Typography>
              <Box
                component="img"
                src={previewImg.src}
                alt={previewImg.name}
                sx={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

export function InventoryLocator() {
  const [term, setTerm] = useState("");
  const [search, setSearch] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [detailCategory, setDetailCategory] = useState<string | null>(null);
  const [detailFilter, setDetailFilter] = useState<DetailFilter>("all");

  const openDetail = (category: string, filter: DetailFilter) => {
    setDetailCategory(category);
    setDetailFilter(filter);
  };

  const rollupQuery = useQuery({
    queryKey: furnitureKeys.categoryRollup(search),
    queryFn: () => inventoryApi.listCategoryRollup({ search: search ?? undefined }),
    enabled: search !== null,
  });

  const rollup = rollupQuery.data ?? [];

  const handleShow = () => {
    if (search === term) {
      void rollupQuery.refetch();
    } else {
      setSearch(term);
    }
  };

  return (
    <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Inventory
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
        <IconButton onClick={() => setHelpOpen(true)} color="primary">
          <InfoIcon />
        </IconButton>
        <TextField
          sx={{ flex: 1, minWidth: 160 }}
          placeholder="Locate item"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleShow();
          }}
        />
        <IconButton onClick={handleShow} sx={{ display: { xs: "inline-flex", sm: "none" } }}>
          <SearchIcon />
        </IconButton>
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={handleShow}
          sx={{ display: { xs: "none", sm: "inline-flex" } }}
        >
          Show
        </Button>
      </Box>
      {search !== null && (
        <Box sx={{ mt: 2 }}>
          {rollupQuery.isLoading && <CircularProgress size={20} />}
          {rollupQuery.isError && <Typography color="error">Failed to load inventory</Typography>}
          {!rollupQuery.isLoading && rollup.length > 0 && (
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: { xs: 480, sm: 0 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell>CATEGORY</TableCell>
                    <TableCell align="right">IN</TableCell>
                    <TableCell align="right">OUT</TableCell>
                    <TableCell align="right">SELECTED</TableCell>
                    <TableCell align="right">AVAIL./TOTAL</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rollup.map((r) => (
                    <TableRow
                      key={r.category}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => openDetail(r.category, "all")}
                    >
                      <TableCell sx={{ "&:hover": { bgcolor: "action.selected" } }}>
                        {r.category}
                      </TableCell>
                      <TableCell
                        align="right"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDetail(r.category, "in");
                        }}
                        sx={{ "&:hover": { bgcolor: "action.selected" } }}
                      >
                        {r.in}
                      </TableCell>
                      <TableCell
                        align="right"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDetail(r.category, "out");
                        }}
                        sx={{ "&:hover": { bgcolor: "action.selected" } }}
                      >
                        {r.out}
                      </TableCell>
                      <TableCell
                        align="right"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDetail(r.category, "selected");
                        }}
                        sx={{ "&:hover": { bgcolor: "action.selected" } }}
                      >
                        {r.selected}
                      </TableCell>
                      <TableCell align="right">
                        {r.available}/{r.total}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {!rollupQuery.isLoading && rollup.length === 0 && (
            <Typography color="text.secondary">No matches.</Typography>
          )}
        </Box>
      )}
      <SearchHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
      <DetailsDialog
        category={detailCategory}
        filter={detailFilter}
        search={search}
        onClose={() => {
          setDetailCategory(null);
          setDetailFilter("all");
        }}
      />
    </Paper>
  );
}
